'use client';

import React, { useRef, useEffect, useCallback, useState, forwardRef, useImperativeHandle } from 'react';
import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { PixelateShader, PetrifyShader, ToadShader, HasteShader, CharmedShader } from './shaders';
import { composeDragConfig, initializeDynamicHazards, cleanupDynamicHazards } from './dragUtils';
import { PhotoSphereViewerProps, PhotoSphereViewerAPI, DynamicDragState, PhotoSpherePosition } from './types';
import { GameHazard } from '@/lib/types/common/GameMode';
import { useAnimatedHazardStyle } from '@/hooks/useAnimatedHazardStyle';

// Overlays for specific hazards
import BlindOverlay from '@/app/(front)/components/GamePlay/BlindOverlay';
import SleepOverlay from '@/app/(front)/components/GamePlay/SleepOverlay';
import CharmedOverlay from '@/app/(front)/components/GamePlay/CharmedOverlay';
import MascotOverlay from '@/app/(front)/components/GamePlay/MascotOverlay';
import { yawStore } from '@/app/hooks/useYawStore';

const PhotoSphereViewer = forwardRef<PhotoSphereViewerAPI, PhotoSphereViewerProps>((props, ref) => {
    const {
        src,
        height,
        width,
        hazards = [],
        showHazards = false,
        autoRotate = false,
        autoRotateSpeed = 0.1,
        // shader is now auto-selected based on hazards
        shaderSettings = {},
        onYawChange,
        onReady,
        onLoadStart,
        onLoadComplete,
        defaultYaw = 0,
        defaultPitch = 0,
        defaultZoom = 75, // FOV in degrees
        dampingFactor = 0.2,
        sensitivity = 0.001,
    } = props;

    const containerRef = useRef<HTMLDivElement>(null);
    const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
    const sceneRef = useRef<THREE.Scene | null>(null);
    const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
    const composerRef = useRef<EffectComposer | null>(null);
    const sphereRef = useRef<THREE.Mesh | null>(null);
    const animationFrameRef = useRef<number | null>(null);
    const shaderPassesRef = useRef<ShaderPass[]>([]);

    // Drag state
    const isDraggingRef = useRef(false);
    const lastPointerRef = useRef({ x: 0, y: 0 });
    const velocityRef = useRef({ yaw: 0, pitch: 0 });
    const positionRef = useRef({ yaw: defaultYaw, pitch: defaultPitch });
    const targetPositionRef = useRef({ yaw: defaultYaw, pitch: defaultPitch });
    const dynamicStateRef = useRef<DynamicDragState>({ inversions: new Map(), intervals: new Map() });
    const lastMoveTimeRef = useRef(0);
    const velocityHistoryRef = useRef<Array<{ yaw: number; pitch: number; time: number }>>([]);

    // Pinch zoom state for mobile
    const lastPinchDistanceRef = useRef<number | null>(null);
    const activeTouchesRef = useRef<Map<number, { x: number; y: number }>>(new Map());

    // Shader animation refs
    const shaderTimeRef = useRef(0);
    const lastFrameTimeRef = useRef(0);
    const currentDragSpeedRef = useRef(0);
    const currentDragDirectionRef = useRef({ x: 0, y: 0 });

    // State
    const [isLoading, setIsLoading] = useState(true);
    const [isAutoRotating, setIsAutoRotating] = useState(autoRotate);
    const isAutoRotatingRef = useRef(autoRotate);

    // Refs for values that need to be accessed in animation loop
    const hazardsRef = useRef(hazards);
    const showHazardsRef = useRef(showHazards);

    // Charmed hazard state - stores the target position the camera should rotate towards
    const charmedTargetRef = useRef<{ yaw: number; pitch: number } | null>(null);
    const [currentViewerPosition, setCurrentViewerPosition] = useState({ yaw: defaultYaw, pitch: defaultPitch });

    // Throttle yaw change callbacks to prevent blocking map interactions
    const lastYawChangeRef = useRef<number>(0);
    const lastReportedYawRef = useRef<number>(defaultYaw);
    const YAW_CHANGE_THROTTLE_MS = 10; // Only report yaw changes every 50ms
    const YAW_CHANGE_THRESHOLD = 0.01; // Only report if yaw changed by more than this

    // Velocity history window in ms - we average velocities over this period
    const VELOCITY_WINDOW_MS = 100;
    // If pointer hasn't moved for this long, consider it stopped
    const STOP_THRESHOLD_MS = 80;
    // Interpolation factor for smooth panning (0-1, lower = smoother)
    const POSITION_LERP_FACTOR = 0.15;

    // Expose API via ref
    useImperativeHandle(ref, () => ({
        getPosition: () => ({ ...positionRef.current }),
        setPosition: (position: Partial<PhotoSpherePosition>) => {
            if (position.yaw !== undefined) {
                positionRef.current.yaw = position.yaw;
                targetPositionRef.current.yaw = position.yaw;
            }
            if (position.pitch !== undefined) {
                positionRef.current.pitch = position.pitch;
                targetPositionRef.current.pitch = position.pitch;
            }
            updateCamera();
        },
        startAutoRotate: () => setIsAutoRotating(true),
        stopAutoRotate: () => setIsAutoRotating(false),
        reset: () => {
            positionRef.current = { yaw: defaultYaw, pitch: defaultPitch };
            targetPositionRef.current = { yaw: defaultYaw, pitch: defaultPitch };
            velocityRef.current = { yaw: 0, pitch: 0 };
            updateCamera();
        },
        dispose: () => cleanup(),
    }));

    // Get drag configuration based on active hazards
    // Drag config ref to always have up-to-date config
    const dragConfigRef = useRef<import('./types').DragConfig>({
        yawMultiplier: 1,
        pitchMultiplier: 1,
        yawInverted: false,
        pitchInverted: false,
        smoothFactor: undefined,
        yawDisabled: false,
        forcedAutoRotate: false,
        forcedAutoRotateSpeed: undefined,
        charmedTarget: false,
        charmedRotateSpeed: undefined,
    });

    // Update drag config whenever hazards or showHazards change
    useEffect(() => {
        // Keep refs in sync with props
        hazardsRef.current = hazards;
        showHazardsRef.current = showHazards;

        if (!showHazards) {
            dragConfigRef.current = {
                yawMultiplier: 1,
                pitchMultiplier: 1,
                yawInverted: false,
                pitchInverted: false,
                smoothFactor: undefined,
                yawDisabled: false,
                forcedAutoRotate: false,
                forcedAutoRotateSpeed: undefined,
                charmedTarget: false,
                charmedRotateSpeed: undefined,
            };
        } else {
            dragConfigRef.current = composeDragConfig(hazards, showHazards, dynamicStateRef.current);
        }

        // Reset charmed target when hazards change
        if (!hazards.includes(GameHazard.CHARMED) || !showHazards) {
            charmedTargetRef.current = null;
        }
    }, [hazards, showHazards]);

    // Callback for when CharmedOverlay generates a target position
    const handleCharmedTargetGenerated = useCallback((yaw: number, pitch: number) => {
        charmedTargetRef.current = { yaw, pitch };
    }, []);

    // Update camera based on current position
    const updateCamera = useCallback(() => {
        if (!cameraRef.current) return;

        const { yaw, pitch } = positionRef.current;

        // Convert spherical to cartesian
        // yaw rotates around Y axis, pitch tilts up/down
        const x = Math.sin(yaw) * Math.cos(pitch);
        const y = Math.sin(pitch);
        const z = Math.cos(yaw) * Math.cos(pitch);

        cameraRef.current.lookAt(x, y, z);

        // Throttle yaw change notifications to prevent blocking map interactions
        const now = performance.now();
        const yawDiff = Math.abs(yaw - lastReportedYawRef.current);
        if (now - lastYawChangeRef.current >= YAW_CHANGE_THROTTLE_MS && yawDiff >= YAW_CHANGE_THRESHOLD) {
            lastYawChangeRef.current = now;
            lastReportedYawRef.current = yaw;
            // Update the yaw store directly (for Compass and other subscribers)
            yawStore.setYaw(yaw);
            onYawChange?.(yaw);
        }
    }, [onYawChange]);

    // Handle pointer down
    const handlePointerDown = useCallback((e: PointerEvent) => {
        e.preventDefault();
        isDraggingRef.current = true;
        lastPointerRef.current = { x: e.clientX, y: e.clientY };
        velocityRef.current = { yaw: 0, pitch: 0 };
        velocityHistoryRef.current = [];
        lastMoveTimeRef.current = performance.now();

        // Sync target to current position to prevent jumps
        targetPositionRef.current = { ...positionRef.current };

        // Stop autorotate on interaction (but NOT forced autorotate from hazards)
        const config = dragConfigRef.current;
        if (isAutoRotating && !config.forcedAutoRotate) {
            setIsAutoRotating(false);
        }

        // Capture pointer
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
    }, [isAutoRotating]);

    // Handle pointer move
    const handlePointerMove = useCallback((e: PointerEvent) => {
        if (!isDraggingRef.current) return;

        const now = performance.now();
        const dx = e.clientX - lastPointerRef.current.x;
        const dy = e.clientY - lastPointerRef.current.y;

        const config = dragConfigRef.current;

        // Get current container size
        let width = 1000, height = 1000;
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            width = rect.width;
            height = rect.height;
        }
        // Normalize sensitivity to a base size (1000px)
        const baseSizeWidth = 2000;
        const baseSizeHeight = 1300;
        const widthFactor = width / baseSizeWidth;
        const heightFactor = height / baseSizeHeight;

        // Adjust sensitivity based on FOV - slower rotation when zoomed in
        // Base FOV is 75, so we scale relative to that
        const baseFOV = 75;
        const currentFOV = cameraRef.current?.fov ?? baseFOV;
        const fovFactor = currentFOV / baseFOV;

        // Calculate position delta, scaled by viewer size, FOV, and hazard multipliers
        let deltaYaw = dx * sensitivity * fovFactor / widthFactor;
        let deltaPitch = dy * sensitivity * fovFactor / heightFactor;

        // Apply hazard multipliers
        deltaYaw *= config.yawMultiplier ?? 1;
        deltaPitch *= config.pitchMultiplier ?? 1;

        // Apply inversions
        if (config.yawInverted) deltaYaw = -deltaYaw;
        if (config.pitchInverted) deltaPitch = -deltaPitch;

        // Disable yaw drag if configured (for FORCED_MARCH hazard)
        if (config.yawDisabled) deltaYaw = 0;

        // Update TARGET position (actual position will interpolate towards it)
        targetPositionRef.current.yaw += deltaYaw;
        targetPositionRef.current.pitch += deltaPitch;

        // Clamp target pitch to avoid gimbal lock
        targetPositionRef.current.pitch = Math.max(-Math.PI / 2 + 0.01, Math.min(Math.PI / 2 - 0.01, targetPositionRef.current.pitch));

        // Record velocity for inertia calculation
        velocityHistoryRef.current.push({
            yaw: deltaYaw,
            pitch: deltaPitch,
            time: now,
        });

        // Remove old velocity samples outside the window
        const cutoff = now - VELOCITY_WINDOW_MS;
        velocityHistoryRef.current = velocityHistoryRef.current.filter(v => v.time > cutoff);

        // Update drag speed and direction for shader (Haste effect)
        const rawSpeed = Math.sqrt(dx * dx + dy * dy);
        currentDragSpeedRef.current = Math.min(rawSpeed * 0.05, 2.0); // Scale and clamp
        if (rawSpeed > 0.5) {
            currentDragDirectionRef.current = { x: dx / rawSpeed, y: -dy / rawSpeed };
        }

        lastPointerRef.current = { x: e.clientX, y: e.clientY };
        lastMoveTimeRef.current = now;
    }, [sensitivity]);

    // Handle pointer up
    const handlePointerUp = useCallback((e: PointerEvent) => {
        if (!isDraggingRef.current) return;

        isDraggingRef.current = false;
        (e.target as HTMLElement).releasePointerCapture(e.pointerId);

        const now = performance.now();
        const timeSinceLastMove = now - lastMoveTimeRef.current;

        // If the pointer hasn't moved recently, don't apply inertia
        if (timeSinceLastMove > STOP_THRESHOLD_MS) {
            velocityRef.current = { yaw: 0, pitch: 0 };
            velocityHistoryRef.current = [];
            return;
        }

        // Calculate average velocity from recent history
        const recentVelocities = velocityHistoryRef.current.filter(
            v => v.time > now - VELOCITY_WINDOW_MS
        );

        if (recentVelocities.length > 0) {
            const avgYaw = recentVelocities.reduce((sum, v) => sum + v.yaw, 0) / recentVelocities.length;
            const avgPitch = recentVelocities.reduce((sum, v) => sum + v.pitch, 0) / recentVelocities.length;

            // Scale up slightly since these are per-frame deltas and we want momentum
            velocityRef.current = {
                yaw: avgYaw * 1.2,
                pitch: avgPitch * 1.2,
            };
        } else {
            velocityRef.current = { yaw: 0, pitch: 0 };
        }

        velocityHistoryRef.current = [];
    }, []);

    // Handle wheel for zoom
    const handleWheel = useCallback((e: WheelEvent) => {
        e.preventDefault();
        if (!cameraRef.current) return;

        const zoomDelta = e.deltaY * 0.05;
        cameraRef.current.fov = Math.max(30, Math.min(100, cameraRef.current.fov + zoomDelta));
        cameraRef.current.updateProjectionMatrix();
    }, []);

    // Helper function to calculate distance between two touch points
    const getTouchDistance = useCallback((touch1: { x: number; y: number }, touch2: { x: number; y: number }) => {
        const dx = touch1.x - touch2.x;
        const dy = touch1.y - touch2.y;
        return Math.sqrt(dx * dx + dy * dy);
    }, []);

    // Handle touch start for mobile
    const handleTouchStart = useCallback((e: TouchEvent) => {
        e.preventDefault();
        
        // Track all active touches
        for (let i = 0; i < e.changedTouches.length; i++) {
            const touch = e.changedTouches[i];
            activeTouchesRef.current.set(touch.identifier, { x: touch.clientX, y: touch.clientY });
        }

        if (activeTouchesRef.current.size === 1) {
            // Single touch - start drag
            const touch = e.touches[0];
            isDraggingRef.current = true;
            lastPointerRef.current = { x: touch.clientX, y: touch.clientY };
            velocityRef.current = { yaw: 0, pitch: 0 };
            velocityHistoryRef.current = [];
            lastMoveTimeRef.current = performance.now();
            targetPositionRef.current = { ...positionRef.current };

            const config = dragConfigRef.current;
            if (isAutoRotating && !config.forcedAutoRotate) {
                setIsAutoRotating(false);
            }
        } else if (activeTouchesRef.current.size === 2) {
            // Two touches - prepare for pinch zoom
            const touches = Array.from(activeTouchesRef.current.values());
            lastPinchDistanceRef.current = getTouchDistance(touches[0], touches[1]);
            isDraggingRef.current = false; // Stop drag while pinching
        }
    }, [isAutoRotating, getTouchDistance]);

    // Handle touch move for mobile
    const handleTouchMove = useCallback((e: TouchEvent) => {
        e.preventDefault();

        // Update tracked touches
        for (let i = 0; i < e.changedTouches.length; i++) {
            const touch = e.changedTouches[i];
            if (activeTouchesRef.current.has(touch.identifier)) {
                activeTouchesRef.current.set(touch.identifier, { x: touch.clientX, y: touch.clientY });
            }
        }

        if (activeTouchesRef.current.size === 2 && lastPinchDistanceRef.current !== null) {
            // Handle pinch zoom
            const touches = Array.from(activeTouchesRef.current.values());
            const currentDistance = getTouchDistance(touches[0], touches[1]);
            const pinchDelta = lastPinchDistanceRef.current - currentDistance;

            if (cameraRef.current) {
                const zoomDelta = pinchDelta * 0.1;
                cameraRef.current.fov = Math.max(30, Math.min(100, cameraRef.current.fov + zoomDelta));
                cameraRef.current.updateProjectionMatrix();
            }

            lastPinchDistanceRef.current = currentDistance;
        } else if (activeTouchesRef.current.size === 1 && isDraggingRef.current) {
            // Handle single touch drag (same logic as pointer move)
            const touch = e.touches[0];
            const now = performance.now();
            const dx = touch.clientX - lastPointerRef.current.x;
            const dy = touch.clientY - lastPointerRef.current.y;

            const config = dragConfigRef.current;

            let width = 1000, height = 1000;
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                width = rect.width;
                height = rect.height;
            }

            const baseSizeWidth = 2000;
            const baseSizeHeight = 1300;
            const widthFactor = width / baseSizeWidth;
            const heightFactor = height / baseSizeHeight;

            const baseFOV = 75;
            const currentFOV = cameraRef.current?.fov ?? baseFOV;
            const fovFactor = currentFOV / baseFOV;

            let deltaYaw = dx * sensitivity * fovFactor / widthFactor;
            let deltaPitch = dy * sensitivity * fovFactor / heightFactor;

            deltaYaw *= config.yawMultiplier ?? 1;
            deltaPitch *= config.pitchMultiplier ?? 1;

            if (config.yawInverted) deltaYaw = -deltaYaw;
            if (config.pitchInverted) deltaPitch = -deltaPitch;
            if (config.yawDisabled) deltaYaw = 0;

            targetPositionRef.current.yaw += deltaYaw;
            targetPositionRef.current.pitch += deltaPitch;
            targetPositionRef.current.pitch = Math.max(-Math.PI / 2 + 0.01, Math.min(Math.PI / 2 - 0.01, targetPositionRef.current.pitch));

            velocityHistoryRef.current.push({ yaw: deltaYaw, pitch: deltaPitch, time: now });
            const cutoff = now - VELOCITY_WINDOW_MS;
            velocityHistoryRef.current = velocityHistoryRef.current.filter(v => v.time > cutoff);

            const rawSpeed = Math.sqrt(dx * dx + dy * dy);
            currentDragSpeedRef.current = Math.min(rawSpeed * 0.05, 2.0);
            if (rawSpeed > 0.5) {
                currentDragDirectionRef.current = { x: dx / rawSpeed, y: -dy / rawSpeed };
            }

            lastPointerRef.current = { x: touch.clientX, y: touch.clientY };
            lastMoveTimeRef.current = now;
        }
    }, [sensitivity, getTouchDistance]);

    // Handle touch end for mobile
    const handleTouchEnd = useCallback((e: TouchEvent) => {
        e.preventDefault();

        // Remove ended touches from tracking
        for (let i = 0; i < e.changedTouches.length; i++) {
            const touch = e.changedTouches[i];
            activeTouchesRef.current.delete(touch.identifier);
        }

        if (activeTouchesRef.current.size === 0) {
            // All touches ended - apply inertia
            const now = performance.now();
            const timeSinceLastMove = now - lastMoveTimeRef.current;

            if (timeSinceLastMove > STOP_THRESHOLD_MS) {
                velocityRef.current = { yaw: 0, pitch: 0 };
            } else {
                const recentVelocities = velocityHistoryRef.current.filter(
                    v => v.time > now - VELOCITY_WINDOW_MS
                );

                if (recentVelocities.length > 0) {
                    const avgYaw = recentVelocities.reduce((sum, v) => sum + v.yaw, 0) / recentVelocities.length;
                    const avgPitch = recentVelocities.reduce((sum, v) => sum + v.pitch, 0) / recentVelocities.length;
                    velocityRef.current = { yaw: avgYaw * 1.2, pitch: avgPitch * 1.2 };
                } else {
                    velocityRef.current = { yaw: 0, pitch: 0 };
                }
            }

            isDraggingRef.current = false;
            lastPinchDistanceRef.current = null;
            velocityHistoryRef.current = [];
        } else if (activeTouchesRef.current.size === 1) {
            // Went from pinch to single touch - resume drag
            const touch = Array.from(activeTouchesRef.current.values())[0];
            isDraggingRef.current = true;
            lastPointerRef.current = { x: touch.x, y: touch.y };
            lastPinchDistanceRef.current = null;
            velocityHistoryRef.current = [];
            lastMoveTimeRef.current = performance.now();
        }
    }, []);

    // Animation loop
    const animate = useCallback(() => {
        if (!rendererRef.current || !sceneRef.current || !cameraRef.current) return;

        animationFrameRef.current = requestAnimationFrame(animate);

        // Update delta time for shader animations
        const now = performance.now();
        const deltaTime = lastFrameTimeRef.current > 0 ? (now - lastFrameTimeRef.current) / 1000 : 0.016;
        lastFrameTimeRef.current = now;
        shaderTimeRef.current += deltaTime;

        // Update shader uniforms for all active shader passes
        for (const shaderPass of shaderPassesRef.current) {
            const uniforms = shaderPass.uniforms;

            // Update time for idle animations
            if (uniforms.time !== undefined) {
                uniforms.time.value = shaderTimeRef.current;
            }

            // Update drag-based uniforms for Haste shader
            if (uniforms.dragSpeed !== undefined) {
                // Smooth the drag speed (decay when not dragging)
                if (!isDraggingRef.current) {
                    currentDragSpeedRef.current *= 0.9; // Decay
                    if (currentDragSpeedRef.current < 0.01) {
                        currentDragSpeedRef.current = 0;
                    }
                }
                uniforms.dragSpeed.value = currentDragSpeedRef.current;
            }

            if (uniforms.dragDirection !== undefined) {
                uniforms.dragDirection.value.set(
                    currentDragDirectionRef.current.x,
                    currentDragDirectionRef.current.y
                );
            }
        }

        // Apply inertia to TARGET when not dragging
        if (!isDraggingRef.current) {
            // Apply velocity with damping to target position
            targetPositionRef.current.yaw += velocityRef.current.yaw;
            targetPositionRef.current.pitch += velocityRef.current.pitch;

            // Clamp target pitch
            targetPositionRef.current.pitch = Math.max(-Math.PI / 2 + 0.01, Math.min(Math.PI / 2 - 0.01, targetPositionRef.current.pitch));

            // Damping
            velocityRef.current.yaw *= dampingFactor;
            velocityRef.current.pitch *= dampingFactor;

            // Stop very small velocities
            if (Math.abs(velocityRef.current.yaw) < 0.00001) velocityRef.current.yaw = 0;
            if (Math.abs(velocityRef.current.pitch) < 0.00001) velocityRef.current.pitch = 0;

            // Apply autorotate if enabled and no velocity
            if (isAutoRotatingRef.current && velocityRef.current.yaw === 0 && velocityRef.current.pitch === 0) {
                targetPositionRef.current.yaw -= autoRotateSpeed * (1 / 60);
                // Snap pitch back to 0 smoothly
                targetPositionRef.current.pitch += (0 - targetPositionRef.current.pitch) * 0.0007;
            }
        }

        // Apply forced autorotate from hazard (FORCED_MARCH) - always rotates regardless of user interaction
        // This is OUTSIDE the !isDragging check so it runs even while dragging
        const dragConfig = dragConfigRef.current;
        if (dragConfig.forcedAutoRotate) {
            const forcedSpeed = dragConfig.forcedAutoRotateSpeed ?? 0.3;
            targetPositionRef.current.yaw -= forcedSpeed * (1 / 60);
        }

        // Apply charmed effect - auto-rotate towards the heart target
        // This runs even while dragging, constantly pulling the view towards the target
        if (dragConfig.charmedTarget && charmedTargetRef.current) {
            const target = charmedTargetRef.current;
            const charmedSpeed = dragConfig.charmedRotateSpeed ?? 0.5;

            // Calculate the angular difference to the target
            let deltaYaw = target.yaw - targetPositionRef.current.yaw;
            const deltaPitch = target.pitch - targetPositionRef.current.pitch;

            // Normalize deltaYaw to -PI to PI range (shortest path)
            while (deltaYaw > Math.PI) deltaYaw -= 2 * Math.PI;
            while (deltaYaw < -Math.PI) deltaYaw += 2 * Math.PI;

            // Apply a force towards the target (scaled by distance for smoother approach)
            const distance = Math.sqrt(deltaYaw * deltaYaw + deltaPitch * deltaPitch);
            const pullStrength = charmedSpeed * (1 / 60);

            if (distance > 0.01) {
                // Normalize and apply pull force
                targetPositionRef.current.yaw += (deltaYaw / distance) * pullStrength * Math.min(distance, 1);
                targetPositionRef.current.pitch += (deltaPitch / distance) * pullStrength * Math.min(distance, 1);
            }
        }

        // Interpolate current position towards target position
        // Note: Multipliers and inversions are already applied in handlePointerMove when updating targetPosition
        // Here we just smoothly interpolate towards the target
        const lerpFactor = dragConfig.smoothFactor !== undefined ? dragConfig.smoothFactor : POSITION_LERP_FACTOR;

        // Simple linear interpolation towards target - no need to apply inversions again
        const yawDelta = (targetPositionRef.current.yaw - positionRef.current.yaw) * lerpFactor;
        const pitchDelta = (targetPositionRef.current.pitch - positionRef.current.pitch) * lerpFactor;
        positionRef.current.yaw += yawDelta;
        positionRef.current.pitch += pitchDelta;

        // Clamp actual pitch
        positionRef.current.pitch = Math.max(-Math.PI / 2 + 0.01, Math.min(Math.PI / 2 - 0.01, positionRef.current.pitch));

        // Update state for CharmedOverlay to track current position (after position is updated)
        // Use refs to get current values since animate() closure may be stale
        if (hazardsRef.current.includes(GameHazard.CHARMED) && showHazardsRef.current) {
            setCurrentViewerPosition({
                yaw: positionRef.current.yaw,
                pitch: positionRef.current.pitch,
            });
        }

        updateCamera();

        // Render
        if (composerRef.current) {
            composerRef.current.render();
        } else {
            rendererRef.current.render(sceneRef.current, cameraRef.current);
        }
    }, [dampingFactor, autoRotateSpeed, updateCamera]);

    // Initialize Three.js scene
    const initScene = useCallback(() => {
        if (!containerRef.current) return;

        const container = containerRef.current;
        const rect = container.getBoundingClientRect();

        // Create renderer
        const renderer = new THREE.WebGLRenderer({ antialias: true });
        renderer.setSize(rect.width, rect.height);
        renderer.setPixelRatio(window.devicePixelRatio);
        container.appendChild(renderer.domElement);
        rendererRef.current = renderer;

        // Create scene
        const scene = new THREE.Scene();
        sceneRef.current = scene;

        // Create camera
        const camera = new THREE.PerspectiveCamera(
            defaultZoom,
            rect.width / rect.height,
            0.1,
            1000
        );
        camera.position.set(0, 0, 0);
        cameraRef.current = camera;

        // Create sphere geometry (inside-out)
        const geometry = new THREE.SphereGeometry(500, 60, 40);
        geometry.scale(-1, 1, 1); // Flip normals inward

        // Create material (placeholder)
        const material = new THREE.MeshBasicMaterial({ color: 0x000000 });
        const sphere = new THREE.Mesh(geometry, material);
        scene.add(sphere);
        sphereRef.current = sphere;

        // Setup event listeners
        renderer.domElement.addEventListener('pointerdown', handlePointerDown);
        renderer.domElement.addEventListener('pointermove', handlePointerMove);
        renderer.domElement.addEventListener('pointerup', handlePointerUp);
        renderer.domElement.addEventListener('pointerleave', handlePointerUp);
        renderer.domElement.addEventListener('wheel', handleWheel, { passive: false });

        // Touch event listeners for mobile support
        renderer.domElement.addEventListener('touchstart', handleTouchStart, { passive: false });
        renderer.domElement.addEventListener('touchmove', handleTouchMove, { passive: false });
        renderer.domElement.addEventListener('touchend', handleTouchEnd, { passive: false });
        renderer.domElement.addEventListener('touchcancel', handleTouchEnd, { passive: false });

        // Set cursor style
        renderer.domElement.style.cursor = 'grab';

        // Set initial camera position
        updateCamera();
    }, [defaultZoom, handlePointerDown, handlePointerMove, handlePointerUp, handleWheel, handleTouchStart, handleTouchMove, handleTouchEnd, updateCamera]);

    // Setup shader post-processing
    const setupShader = useCallback(() => {
        if (!rendererRef.current || !sceneRef.current || !cameraRef.current) return;

        // Clear existing composer and shader passes
        composerRef.current = null;
        shaderPassesRef.current = [];

        if (!showHazards) return;

        // Collect all active shaders (use any for shader type since they have different uniforms)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const activeShaders: any[] = [];

        if (hazards.includes(GameHazard.PIXELATE)) {
            activeShaders.push(PixelateShader);
        }
        if (hazards.includes(GameHazard.PETRIFY)) {
            activeShaders.push(PetrifyShader);
        }
        if (hazards.includes(GameHazard.TOAD)) {
            activeShaders.push(ToadShader);
        }
        if (hazards.includes(GameHazard.HASTE)) {
            activeShaders.push(HasteShader);
        }
        if (hazards.includes(GameHazard.CHARMED)) {
            activeShaders.push(CharmedShader);
        }

        // If no shader hazards, return without creating a composer
        if (activeShaders.length === 0) return;

        // Create composer with render pass
        const composer = new EffectComposer(rendererRef.current);
        const renderPass = new RenderPass(sceneRef.current, cameraRef.current);
        composer.addPass(renderPass);

        // Get container dimensions for resolution uniform
        const rect = containerRef.current?.getBoundingClientRect();

        // Add shader passes for each active hazard
        for (const shader of activeShaders) {
            const shaderPass = new ShaderPass(shader);
            
            // Set resolution uniform if the shader has one
            if (shaderPass.uniforms.resolution && rect) {
                shaderPass.uniforms.resolution.value.set(rect.width, rect.height);
            }
            
            composer.addPass(shaderPass);
            shaderPassesRef.current.push(shaderPass);
        }

        composerRef.current = composer;
    }, [hazards, showHazards, shaderSettings]);

    // Load texture
    const loadTexture = useCallback(() => {
        if (!sphereRef.current) return;

        setIsLoading(true);
        onLoadStart?.();

        const loader = new THREE.TextureLoader();
        loader.load(
            src,
            (texture) => {
                texture.colorSpace = THREE.SRGBColorSpace;
                texture.minFilter = THREE.LinearFilter;
                texture.magFilter = THREE.LinearFilter;

                const material = new THREE.MeshBasicMaterial({ map: texture });
                sphereRef.current!.material = material;

                setIsLoading(false);
                onLoadComplete?.();
                onReady?.();
            },
            undefined,
            (error) => {
                console.error('Failed to load photosphere texture:', error);
                setIsLoading(false);
            }
        );
    }, [src, onLoadStart, onLoadComplete, onReady]);

    // Cleanup function
    const cleanup = useCallback(() => {
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }

        if (rendererRef.current) {
            rendererRef.current.domElement.removeEventListener('pointerdown', handlePointerDown);
            rendererRef.current.domElement.removeEventListener('pointermove', handlePointerMove);
            rendererRef.current.domElement.removeEventListener('pointerup', handlePointerUp);
            rendererRef.current.domElement.removeEventListener('pointerleave', handlePointerUp);
            rendererRef.current.domElement.removeEventListener('wheel', handleWheel);
            rendererRef.current.domElement.removeEventListener('touchstart', handleTouchStart);
            rendererRef.current.domElement.removeEventListener('touchmove', handleTouchMove);
            rendererRef.current.domElement.removeEventListener('touchend', handleTouchEnd);
            rendererRef.current.domElement.removeEventListener('touchcancel', handleTouchEnd);

            rendererRef.current.dispose();
            if (containerRef.current && rendererRef.current.domElement.parentNode === containerRef.current) {
                containerRef.current.removeChild(rendererRef.current.domElement);
            }
        }

        if (sphereRef.current) {
            const material = sphereRef.current.material as THREE.MeshBasicMaterial;
            if (material.map) material.map.dispose();
            material.dispose();
            sphereRef.current.geometry.dispose();
        }

        cleanupDynamicHazards(dynamicStateRef.current);

        rendererRef.current = null;
        sceneRef.current = null;
        cameraRef.current = null;
        composerRef.current = null;
        sphereRef.current = null;
    }, [handlePointerDown, handlePointerMove, handlePointerUp, handleWheel, handleTouchStart, handleTouchMove, handleTouchEnd]);

    // Handle resize
    const handleResize = useCallback(() => {
        if (!containerRef.current || !rendererRef.current || !cameraRef.current) return;

        const rect = containerRef.current.getBoundingClientRect();

        cameraRef.current.aspect = rect.width / rect.height;
        cameraRef.current.updateProjectionMatrix();

        rendererRef.current.setSize(rect.width, rect.height);

        if (composerRef.current) {
            composerRef.current.setSize(rect.width, rect.height);
        }

        // Update resolution uniform for all shader passes
        for (const shaderPass of shaderPassesRef.current) {
            if (shaderPass.uniforms.resolution) {
                shaderPass.uniforms.resolution.value.set(rect.width, rect.height);
            }
        }
    }, []);

    // Initialize on mount
    useEffect(() => {
        initScene();
        loadTexture();
        animate();

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            cleanup();
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Reload texture when src changes
    useEffect(() => {
        if (sphereRef.current) {
            loadTexture();
        }
    }, [src]); // eslint-disable-line react-hooks/exhaustive-deps

    // Update shader when hazards or shaderSettings change
    useEffect(() => {
        setupShader();
    }, [hazards, showHazards, shaderSettings.pixelSize]); // eslint-disable-line react-hooks/exhaustive-deps

    // Initialize dynamic hazard behaviors
    useEffect(() => {
        cleanupDynamicHazards(dynamicStateRef.current);

        dynamicStateRef.current = initializeDynamicHazards(
            hazards,
            showHazards,
            (state) => {
                dynamicStateRef.current = state;
            }
        );

        return () => {
            cleanupDynamicHazards(dynamicStateRef.current);
        };
    }, [hazards, showHazards]);

    // Update autorotate state
    useEffect(() => {
        setIsAutoRotating(autoRotate);
        isAutoRotatingRef.current = autoRotate;
    }, [autoRotate]);

    // Reset position when defaultYaw/defaultPitch change
    useEffect(() => {
        positionRef.current = { yaw: defaultYaw, pitch: defaultPitch };
        targetPositionRef.current = { yaw: defaultYaw, pitch: defaultPitch };
        updateCamera();
    }, [defaultYaw, defaultPitch, updateCamera]);

    // Get CSS styles for hazard visual effects with animation support
    const hazardStyles = useAnimatedHazardStyle(hazards, { enabled: showHazards });

    return (
        <div
            ref={containerRef}
            style={{
                height,
                width,
                position: 'relative',
                overflow: 'hidden',
                touchAction: 'none', // Prevent default touch behaviors on mobile
                ...hazardStyles,
            }}
        >
            {/* Mascot Overlay for MASCOT hazard */}
            {hazards.includes(GameHazard.MASCOT) && showHazards && (
                <MascotOverlay />
            )}
            {/* Keyhole Overlay for BLIND hazard */}
            {hazards.includes(GameHazard.BLIND) && showHazards && (
                <BlindOverlay />
            )}

            {/* Sleep Overlay for SLEEP hazard */}
            {hazards.includes(GameHazard.SLEEP) && showHazards && (
                <SleepOverlay />
            )}

            {/* Charmed Overlay for CHARMED hazard */}
            {hazards.includes(GameHazard.CHARMED) && showHazards && (
                <CharmedOverlay
                    onTargetGenerated={handleCharmedTargetGenerated}
                    currentYaw={currentViewerPosition.yaw}
                    currentPitch={currentViewerPosition.pitch}
                />
            )}

            

            {/* Loading indicator */}
            {isLoading && (
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        zIndex: 10,
                    }}
                >
                    <div className="text-white">Loading...</div>
                </div>
            )}
        </div>
    );
});

PhotoSphereViewer.displayName = 'PhotoSphereViewer';

export default PhotoSphereViewer;
